import { useRef, useState, useEffect } from 'react'
import { z } from 'zod'
import { RETURNS_FORM_SCHEMA, RETURNS_FORM_SCHEMA_NEW } from '../constants/returnsStep'
import {
  denormalizeReturnsFormData,
  normalizeReturnsFormData,
} from '../utils/normalizeFormData'
import { getReturnsFormData, saveRetursForm } from '../api/returnsStep'
import { getEntityTypes, getMinistries, getState } from '../api/adminTAN'
import { getDisabledFields } from '../constants/tanFormDisabled'
import { sanitizeForLog } from '../constants/index'
import { isNewFormVersion } from '../utils/formVersionUtils'
import {
  FORM_SECTION_REMOVED_FIELDS,
  resolveFieldDisplayName,
} from '../constants/returnsNewFormFields'

export const useReturnsForm = ({ showSnackbar, defaultSearchCOList }) => {
  const initialRef = useRef({})
  const [touched, setTouched] = useState({})

  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [isDirty, setIsDirty] = useState(false)
  const [loading, setLoading] = useState(false)

  const [stateOptions, setStateOptions] = useState([])
  const [ministryOptions, setMinistryOptions] = useState([])
  const [entityTypeOptions, setEntityTypes] = useState([])

  // Select the correct validation schema based on form version
  const getFormSchema = () => {
    const searchList = Array.isArray(defaultSearchCOList) ? {} : defaultSearchCOList
    const fy = searchList?.year
      || searchList?.financialYear
      || formData?.financialYear
      || ''
    if (!fy) return RETURNS_FORM_SCHEMA
    return isNewFormVersion(fy) ? RETURNS_FORM_SCHEMA_NEW : RETURNS_FORM_SCHEMA
  }

  // Fields that are hidden in new forms — skip validation for these
  const SKIP_VALIDATION_NEW_FORM = [
    'revisedReturn',
    'branchOrDivision',
    'alternateStdCode',
    'alternateTelephoneNo',
    'alternateEmail',
    'paoCode',
    'ddoCode',
    'paoRegistrationNo',
    'ddoRegistrationNo',
    'addressChangedSinceLastReturn',
    'responsiblePersonAddressChanged',
    'responsiblePersonMobile',
    'responsiblePersonEmailAlt',
    'responsiblePersonSTDAlt',
    'responsiblePersonTelephoneAlt',
  ]

  const shouldSkipValidation = (field) => {
    const fy = formData?.financialYear || defaultSearchCOList?.year || defaultSearchCOList?.financialYear
    if (!isNewFormVersion(fy)) return false
    return SKIP_VALIDATION_NEW_FORM.includes(field)
  }

  /* ---------- FETCH LOOKUPS ---------- */

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [statesData, ministriesData, entityTypesData] = await Promise.all(
          [getState(), getMinistries(), getEntityTypes()]
        )
        setStateOptions(statesData || [])
        setMinistryOptions(ministriesData || [])
        setEntityTypes(entityTypesData)
      } catch (error) {
        console.error('Failed to fetch states or ministries:', sanitizeForLog(error?.message || 'Unknown error'))

      }
    }

    fetchLookups()
  }, [])

  /* ---------- FETCH & AUTO FILL ---------- */
  const fetchForm = async ({
    tanMasterUuid,
    defaultSearchCOList,
    page = 1,
    pageSize = 20,
  }) => {
    if (!tanMasterUuid) return

    try {
      setLoading(true)

      const { entityTan, entityName, returnType, formType, quarter, financialYear } =
        defaultSearchCOList || {}

      const payload = {
        pageFilter: {
          includeCount: false,
          currentPage: page,
          pageSize,
          sortField: 'entityTAN',
          sortOrder: 'DESC',
        },
        tanUuid: tanMasterUuid,
        entityTAN: entityTan,
        entityName,
        returnType: returnType?.toUpperCase(),
        formType: formType,
        quarter,
        financialYear: financialYear,
      }

      const resp = await getReturnsFormData(payload)
      if (!resp?.success) {
        const errorMessage = resp?.error || 'Failed to fetch form data'
        return { success: false, errorMessage}
      }

      const normalized = normalizeReturnsFormData(resp.data?.data)
      setFormData(normalized)
      initialRef.current = normalized
      setErrors({})
      setIsDirty(false)
      return true
    } catch (err) {
      showSnackbar('Error fetching form data', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    const formattedValue = value ?? ''

    const newData = {
      ...formData,
      [field]: formattedValue,
    }

    // Handle entity type change
    if (field === 'deductorType') {
      const disabledFields = getDisabledFields(formattedValue)

      Object.keys(disabledFields).forEach(f => {
        if (disabledFields[f]) newData[f] = ''
      })

      setErrors(prev => {
        const e = { ...prev }
        Object.keys(disabledFields).forEach(f => delete e[f])
        return e
      })
    }

    // Handle ministry logic
    if (field === 'ministryName' && formattedValue !== 'OTHERS') {
      newData.ministryNameOther = ''
      setErrors(prev => {
        const e = { ...prev }
        delete e.ministryNameOther
        return e
      })
    }

    const isChanged = formData[field] !== formattedValue

    setFormData(newData)

    if (isChanged) setIsDirty(true)

    // mark touched
    setTouched(prev => ({ ...prev, [field]: true }))

    // Skip validation for fields hidden in new forms
    if (shouldSkipValidation(field)) {
      setErrors(prev => ({ ...prev, [field]: '' }))
      return
    }

    try {
      getFormSchema().pick({ [field]: true }).parse({
        [field]: formattedValue,
      })
      setErrors(prev => ({ ...prev, [field]: '' }))
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(prev => ({
          ...prev,
          [field]: err.issues[0]?.message,
        }))
      }
    }
  }

  const handleFieldBlur = (field, value) => {
    const formattedValue = value ?? ''

    setTouched(prev => ({ ...prev, [field]: true }))

    // Skip validation for fields hidden in new forms
    if (shouldSkipValidation(field)) {
      setErrors(prev => ({ ...prev, [field]: '' }))
      return
    }

    try {
      getFormSchema().pick({ [field]: true }).parse({
        [field]: formattedValue,
      })
      setErrors(prev => ({ ...prev, [field]: '' }))
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(prev => ({
          ...prev,
          [field]: err.issues[0]?.message,
        }))
      }
    }
  }

  /* ---------- SAVE ---------- */
  const saveForm = async () => {
    if (!isDirty) {
      showSnackbar('No changes to save', 'info')
      return true
    }

    try {
      getFormSchema().parse(formData)
      setErrors({})
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors = {}
        err.issues.forEach(i => {
          // Skip errors for fields hidden in new forms
          if (!shouldSkipValidation(i.path[0])) {
            fieldErrors[i.path[0]] = i.message
          }
        })
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors)
          showSnackbar('Please fill all mandatory fields', 'error')
          return false
        }
        setErrors({})
      }
    }
    const { quarter } =
      defaultSearchCOList || {}

    const savePayload = denormalizeReturnsFormData(formData, quarter)

    const response = await saveRetursForm(savePayload)

    if (!response?.success || !response?.data?.success) {
      const errorMessage = response?.data?.errors?.[0]?.message || 'Form save failed'
      showSnackbar(errorMessage, 'error')
      return false
    }

    showSnackbar('Form saved successfully', 'success')
    initialRef.current = formData
    setIsDirty(false)
    return true
  }

  /* ---------- CANCEL ---------- */
  const resetForm = () => {
    setFormData(initialRef.current)
    setErrors({})
    setIsDirty(false)
  }


  return {
    formData,
    errors,
    isDirty,
    loading,
    stateOptions,
    ministryOptions,
    entityTypeOptions,
    touched,

    fetchForm,
    handleChange,
    handleFieldBlur,
    saveForm,
    resetForm,
  }
}
